import { LoginRequest, LoginResponse, SuperAdmin, Permission } from '@/types/shared'
import { apiClient } from './client'
import { getDeviceInfo, DeviceInfo } from '@/utils/device'

export interface LoginWithDeviceRequest extends LoginRequest {
  deviceId?: string;
  fcmToken?: string | null;
  deviceType?: 'ios' | 'android' | 'web';
  deviceName?: string;
  appVersion?: string;
  osVersion?: string;
}

export const authApi = {
  login: async (credentials: LoginRequest, deviceInfo?: Partial<DeviceInfo>): Promise<LoginResponse> => {
    const payload: LoginWithDeviceRequest = {
      ...credentials,
      ...deviceInfo,
    };

    const response = await apiClient.post('/auth/login', payload)
    const data = response.data.data

    return {
      user: data.user,
      accessToken: data.accessToken || data.token, // Super admin returns 'accessToken', regular user returns 'token'
      refreshToken: data.refreshToken || '',
      permissions: data.permissions || [],
      requiresOnboarding: data.requiresOnboarding,
      onboardingStatus: data.onboardingStatus,
      requiresTwoFactor: data.requiresTwoFactor,
      userId: data.userId,
      userType: data.userType
    }
  },

  forgotPassword: async (email?: string, phone?: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/forgot-password', { email, phone })
    return response.data
  },

  resetPassword: async (token: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/reset-password', { token, newPassword })
    return response.data
  },

  loginWithPhonePin: async (
    phone: string,
    pin: string,
    tenantSlug?: string,
    deviceInfo?: Partial<DeviceInfo>
  ): Promise<LoginResponse> => {
    const payload = {
      phone,
      pin,
      tenantSlug,
      ...deviceInfo,
    };

    const response = await apiClient.post('/auth/login', payload)
    const data = response.data.data

    return {
      user: data.user,
      accessToken: data.accessToken || data.token, // Super admin returns 'accessToken', regular user returns 'token'
      refreshToken: data.refreshToken || '',
      permissions: data.permissions || [],
      requiresOnboarding: data.requiresOnboarding,
      onboardingStatus: data.onboardingStatus,
      requiresTwoFactor: data.requiresTwoFactor,
      userId: data.userId,
      userType: data.userType
    }
  },

  logout: async (deviceId?: string): Promise<void> => {
    // Unregister device on logout if deviceId provided
    if (deviceId) {
      try {
        await apiClient.post('/auth/devices/unregister', { deviceId });
      } catch (error) {
        console.warn('Failed to unregister device on logout:', error);
      }
    }
    await apiClient.post('/auth/logout')
  },

  getCurrentUser: async (): Promise<{ user: SuperAdmin; permissions: Permission[] }> => {
    const response = await apiClient.get('/auth/me')
    // Backend returns { success: true, data: { user, permissions } }
    return response.data.data
  },

  refreshToken: async (): Promise<{ accessToken: string; refreshToken: string }> => {
    const refreshToken = localStorage.getItem('refreshToken')
    const response = await apiClient.post('/auth/refresh', { refreshToken })
    // Backend returns { success: true, data: { accessToken, refreshToken } }
    return response.data.data
  },

  // Device management endpoints
  registerDevice: async (deviceInfo: DeviceInfo): Promise<{ success: boolean; device: unknown; isNewDevice: boolean }> => {
    const response = await apiClient.post('/auth/devices/register', deviceInfo);
    return response.data.data;
  },

  unregisterDevice: async (deviceId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/auth/devices/unregister', { deviceId });
    return response.data;
  },

  getUserDevices: async (): Promise<{ devices: unknown[]; count: number }> => {
    const response = await apiClient.get('/auth/devices');
    return response.data.data;
  },

  // Helper to get device info for login
  getDeviceInfoForLogin: getDeviceInfo,
}